<script setup>
import { useMetricsStore } from '../../stores/metrics.store';
import AppLoader from '../../components/AppLoader.vue';
import PageBreadcrumb from '../../components/PageBreadcrumb.vue';
import AppFooter from '@/components/AppFooter.vue';
import _ from 'lodash';
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useDestinationsStore } from '../../stores/destinations.store';
import TablePagination from '@/components/TablePagination.vue';
import ItemsPerPageSelect from '@/components/filters/ItemsPerPageSelect.vue';
import { useDeleteConfirmation, useConfirmBeforeAction } from '@/composables/useDialog';
import ActionMenu from '../../components/ActionMenu.vue';
import { MultiSelect } from '@/components/ui/multi-select';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

import { usePipelinesStore } from '../../stores/pipelines.store';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useUrlFilters } from '@/composables/useUrlFilters';
import { Clock, ChevronsRight, CheckCircle, Search } from 'lucide-vue-next';
import {
    getStatusColor,
    getMetricFormattingInfo,
    formatter,
    getAllMetricsForCategoryAndLevel,
    isJSON,
    userHasAccess,
    formatChartXAxisLabels,
    getTimeRangeLabel,
    createDateFilterFromTimeRange,
    convertFilterKeyToTimeRangeValue
} from '../../utils/utils';
import { FILTER_DEFAULTS, TIME_RANGE_LABELS } from '../../utils/constants';
import ConnectorTab from '../../components/ConnectorTab.vue';
import ErrorMessage from '../../components/ErrorMessage.vue';
import WebSocketService from '../../services/webSocket.service';
import SQLViewsModal from '../../components/SQLViewsModal.vue';
import StatCard from '../../components/global/StatCard.vue';
import { watch } from 'vue';
import Tabs from '@/components/global/Tabs.vue';
import HighChart from '../../components/HighChart.vue';
import TimeRangeFilter from '@/components/filters/TimeRangeFilter.vue';
import SelectDropdown from '@/components/filters/SelectDropdown.vue';
import moment from 'moment-timezone';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/composables/useToast';
import { SkeletonLoader, FormSkeleton } from '@/components/ui/skeleton-loader';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

const router = useRouter();
const route = useRoute();

const props = defineProps(['id']);
const pipelines = usePipelinesStore();
const destinationsStore = useDestinationsStore();
const metrics = useMetricsStore();
const authStore = useAuthStore();

// Breadcrumb management
const { setDynamicLabel, clearDynamicLabel } = useBreadcrumbs();

const { registerFilter } = useUrlFilters({
    defaults: {
        // Pagination
        page: FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE,
        pageSize: FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE_SIZE,

        // Date filters
        selectedTopicsDateFilter: { key: FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME },
        selectedTimeseriesDateFilter: { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H },

        // Time range UI state (for TimeRangeFilter components)
        // Note: For topics we use 'allTime' string key since TimeRangeFilter supports it directly
        selectedTopicsTimeRange: 'allTime', // Special case: allTime doesn't have numeric equivalent
        selectedTimeseriesTimeRange: FILTER_DEFAULTS.TIME_RANGE_VALUES['last24h'],

        // Time unit
        selectedTimeseriesTimeUnit: FILTER_DEFAULTS.TIME_UNITS.HOUR,

        // Topic filters
        topicFilter: null,

        // UI state
        tab: FILTER_DEFAULTS.TABS.STATUS,

        // Metrics selection
        selectedTimeseriesMetrics: []
    },

    // Custom serializers for complex objects
    serializers: {
        selectedTopicsDateFilter: (value) => (value && Object.keys(value).length ? JSON.stringify(value) : null),
        selectedTimeseriesDateFilter: (value) => (value && Object.keys(value).length ? JSON.stringify(value) : null),
        topicFilter: (value) => (value ? JSON.stringify(value) : null),
        selectedTimeseriesMetrics: (value) => (value && value.length ? JSON.stringify(value) : null)
    },

    // Custom deserializers
    deserializers: {
        selectedTopicsDateFilter: (value) => {
            try {
                return value ? JSON.parse(value) : { key: FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME };
            } catch {
                return { key: FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME };
            }
        },
        selectedTimeseriesDateFilter: (value) => {
            try {
                return value ? JSON.parse(value) : { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H };
            } catch {
                return { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H };
            }
        },
        topicFilter: (value) => {
            try {
                return value ? JSON.parse(value) : null;
            } catch {
                return null;
            }
        },
        selectedTimeseriesMetrics: (value) => {
            try {
                return value ? JSON.parse(value) : [];
            } catch {
                return [];
            }
        }
    }
});

// Register all filters and get reactive references
const page = registerFilter('page', 0);
const pageSize = registerFilter('pageSize', 10);
const selectedTopicsDateFilter = registerFilter('selectedTopicsDateFilter', { key: FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME });
const selectedTimeseriesDateFilter = registerFilter('selectedTimeseriesDateFilter', { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H });
const selectedTopicsTimeRange = registerFilter('selectedTopicsTimeRange', 'allTime');
const selectedTimeseriesTimeUnit = registerFilter('selectedTimeseriesTimeUnit', FILTER_DEFAULTS.TIME_UNITS.HOUR);
const topicFilter = registerFilter('topicFilter', null);
const currentTab = registerFilter('tab', FILTER_DEFAULTS.TABS.STATUS);
const selectedTimeseriesMetrics = registerFilter('selectedTimeseriesMetrics', []);

const destinationLatestMetrics = computed(() => {
    return metrics.destinations.allLatest[props.id] || {};
});

const connectorInfo = computed(() => {
    return Object.values(destinationsStore.destinationConnectors || {})
        .flat()
        .find((c) => c.connector === destination.value?.connector);
});

let destination = ref();
let defaultDestination = ref();
let generalLoading = ref(false);
let metricsLoading = ref(false);
let configurationLoading = ref(false);
let actionLoading = ref(false);
let timeseriesLoading = ref(false);
let topicsLoading = ref(false);
const timeUnitOptions = [
    { name: 'Month', value: FILTER_DEFAULTS.TIME_UNITS.MONTH },
    { name: 'Day', value: FILTER_DEFAULTS.TIME_UNITS.DAY },
    { name: 'Hour', value: FILTER_DEFAULTS.TIME_UNITS.HOUR },
    { name: 'Minute', value: FILTER_DEFAULTS.TIME_UNITS.MINUTE }
];

let viewsDownloading = ref(false);
let tabLoading = ref(false);
let viewsLoading = ref(false);
let destinationConfig = ref(null);
let formError = ref('');
let hidePromptTemporary = ref(false);

let canSave = ref(false);

let topicsScript = ref([]);
let selectedTopicsScript = ref([]);
let openViewsModal = ref(false);

const chart = ref();

const selectedMetricKeys = computed({
    get() {
        return selectedTimeseriesMetrics.value.map((metric) => metric.key);
    },
    set(keys) {
        selectedTimeseriesMetrics.value = keys.map((key) => listOfTimeseriesMetricOptions.value.find((metric) => metric.key === key)).filter(Boolean);
    }
});

let plotSpecificMetrics = ref(null);
let specificTopicMetricsToUse = ref(null);

const addWebSocketListeners = () => {
    WebSocketService.addListener({
        topic: 'metrics',
        entityType: 'destinations',
        entityId: props.id,
        callback: metrics.onMetricsData,
        callbackId: 'metrics.onMetricsData',
        startListFrom: page.value * pageSize.value,
        listSize: pageSize.value
    });
};

const removeWebSocketListeners = () => {
    WebSocketService.removeListener({
        topic: 'metrics',
        entityType: 'destinations',
        entityId: props.id,
        callbackId: 'metrics.onMetricsData',
        startListFrom: page.value * pageSize.value,
        listSize: pageSize.value
    });
};

onBeforeUnmount(() => {
    removeWebSocketListeners();
    clearDynamicLabel('destination-details');
});

onBeforeRouteLeave((to, from, next) => {
    if (tabLoading.value || metricsLoading.value || configurationLoading.value || generalLoading.value || timeseriesLoading.value) {
        return next();
    }

    if (!canSave.value || hidePromptTemporary.value) {
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You have unsaved changes.');

    if (answer) {
        return next();
    } else {
        return next(false);
    }
});

onMounted(async () => {
    try {
        isInitializing.value = true;
        generalLoading.value = true;
        tabLoading.value = true;
        configurationLoading.value = true;
        metricsLoading.value = true;

        // Set current tab from restored filter
        setCurrentTab(currentTab.value);

        // Initial data loading using current filter state (includes URL-restored values)
        const connectorMetricsPromise = metrics.destinations.fetch(props.id, {
            topic_list_from: page.value * pageSize.value,
            topic_list_size: pageSize.value,
            level: 'connector',
            time_type:
                !_.isEmpty(selectedTimeseriesDateFilter.value) && selectedTimeseriesDateFilter.value.key !== FILTER_DEFAULTS.DATE_FILTERS.LAST_24H
                    ? 'latest'
                    : null
        });

        // Load timeseries data with current filter state
        const timeseriesPromise = fetchTimeseriesData();

        // Load topics data with current filter state
        const topicMetricsPromise = fetchTopicsData();

        await Promise.all([
            destinationsStore.getById(props.id, route?.query?.customMessage).then(async (data) => {
                destination.value = JSON.parse(JSON.stringify(data ?? {}));
                defaultDestination.value = _.cloneDeep(destination.value);

                if (route?.query?.customMessage && data) {
                    // Remove customMessage from URL after handling
                    const newQuery = { ...route.query };
                    delete newQuery.customMessage;
                    router.replace({ query: newQuery });
                }
                generalLoading.value = false;

                await destinationsStore.fetchConfiguration(destination.value.connector).then(() => {
                    destinationConfig.value = destinationsStore.configurations[destination.value.connector];
                    configurationLoading.value = false;

                    if (destinationConfig.value?.some((cfg) => cfg.tab === 'smt')) {
                        tabs.value.push({ name: 'Single Message Transforms', key: 'smt', href: '#', current: false });
                    }
                });
            }),

            destinationsStore.fetchConnectors(),
            connectorMetricsPromise.then(() => {
                metricsLoading.value = false;
            }),
            timeseriesPromise.then(() => {
                timeseriesLoading.value = false;
            }),
            topicMetricsPromise.then(() => {
                topicsLoading.value = false;
            })
        ]);

        tabLoading.value = false;
        addWebSocketListeners();
    } catch (error) {
        console.error('Error in DestinationDetails onMounted:', error);
    } finally {
        generalLoading.value = false;
        configurationLoading.value = false;
        metricsLoading.value = false;
        tabLoading.value = false;
        timeseriesLoading.value = false;
        topicsLoading.value = false;
        isInitializing.value = false; // Allow watchers to trigger after initial load
    }
});

const setViewsModelOpenStatus = async (topic, value) => {
    openViewsModal.value = value;

    if (value) {
        await setTopicsScript([topic.id]);
    } else {
        await setTopicsScript();
    }
};

async function setTopicsScript(topicIds = []) {
    if (destination.value && destination.value.connector == 'snowflake') {
        viewsLoading.value = true;
        const rawSampleScriptsData = await destinationsStore.getTopicSampleScripts(props.id, topicIds);

        const sampleScriptsData = rawSampleScriptsData.map((topicInfo) => {
            return {
                topicName: topicInfo.topicId,
                scriptName: topicInfo.info?.dynamic_table.display_name,
                scriptValue: topicInfo.info?.dynamic_table.value
            };
        });

        topicsScript.value = sampleScriptsData;

        selectedTopicsScript.value = [
            {
                topicName: topicsScript.value[0]?.topicName,
                scriptName: 'Dynamic tables',
                scriptValue: topicsScript.value.map((item) => item.scriptValue).join('\n\n')
            }
        ];

        viewsLoading.value = false;
    }

    if (topicsScript.value.length <= 0) {
        const viewsIndex = tabs.value.findIndex((tab) => tab.name === 'Views');

        if (viewsIndex !== -1) {
            tabs.value.splice(viewsIndex, 1);
        }
    }
}

const availableMetrics = computed(() => {
    if (!connectorInfo.value?.metrics || _.isEmpty(connectorInfo.value?.metrics)) return null;

    const topicMetrics = getAllMetricsForCategoryAndLevel(connectorInfo.value?.metrics, null, 'topic', 'destinations');
    //const connectorMetrics = getAllMetricsForCategoryAndLevel(connectorInfo.value?.metrics, null, ['connector', 'task'], 'destinations');

    return {
        topic: topicMetrics
        // connector: connectorMetrics
    };
});

const topics = (useSpecificMetrics) => {
    const compValue = computed(() => {
        return (
            Object.keys(useSpecificMetrics?.topics || destinationLatestMetrics?.value?.topics || {})
                ?.map((t) => {
                    const topicName = destination.value?.topics?.find(
                        (x) => t.toLowerCase().includes(x.toLowerCase()) && t.split(x)?.[1] === '' // || !t.split(x)?.[1])
                    );

                    return {
                        id: t,
                        name: topicName
                        // snapshotStatus: topicMetric?.status,
                        // snapshotDisplayStatus: _.capitalize(topicMetric?.status),
                        // snapshotSubmitTimestamp: topicMetric?.submit_timestamp
                    };
                })
                .filter((x) => x) || []
        );
    });

    return compValue.value;
};

const destinationTimeseriesConnectorMetrics = computed(() => {
    return metrics.destinations.allTimeseries[props.id]?.connector || {};
});

const listOfTimeseriesMetricOptions = computed(() => {
    if (generalLoading.value || timeseriesLoading.value) return [];

    const defaultDateFilter =
        selectedTimeseriesDateFilter.value?.key === FILTER_DEFAULTS.DATE_FILTERS.LAST_24H ||
        !selectedTimeseriesDateFilter.value ||
        !selectedTimeseriesDateFilter.value?.key;
    const metricsToUse = defaultDateFilter ? destinationTimeseriesConnectorMetrics.value : plotSpecificMetrics.value?.connector;

    let availableTimeSeriesMetricKeys = Object.keys(metricsToUse || {});

    availableTimeSeriesMetricKeys = availableTimeSeriesMetricKeys
        .map((key) => {
            const metricInfo = getMetricFormattingInfo(key, connectorInfo.value?.metrics);

            if (!metricInfo) {
                return null;
            }

            return {
                key,
                ...metricInfo
            };
        })
        .filter((x) => x && metricsToUse[x.key]?.length);

    // Don't clear metrics selection on API failure to avoid infinite loops
    // The watcher will handle validation of persisted metrics

    return availableTimeSeriesMetricKeys;
});

watch(
    () => listOfTimeseriesMetricOptions.value,
    (newValue) => {
        const defaultTo = ['recordsLag'];

        // Only set default metrics if none are selected AND no persisted metrics from URL
        if (!selectedTimeseriesMetrics.value.length && newValue.length) {
            const defaultMetrics = newValue.filter((x) => defaultTo.includes(x.key));
            selectedTimeseriesMetrics.value = defaultMetrics.length ? defaultMetrics : [newValue[0]];
        }

        // If we have persisted metrics from URL, validate they still exist in available metrics
        else if (selectedTimeseriesMetrics.value.length && newValue.length) {
            const validMetrics = selectedTimeseriesMetrics.value.filter((selected) => newValue.find((available) => available.key === selected.key));
            // Update to full metric objects if we only have keys persisted
            selectedTimeseriesMetrics.value = validMetrics.map(
                (selected) => newValue.find((available) => available.key === selected.key) || selected
            );
        }
    }
);

// Update breadcrumb label when destination name becomes available
watch(
    () => destination.value?.name,
    (newName) => {
        if (newName) {
            setDynamicLabel('destination-details', newName);
        }
    },
    { immediate: true }
);

let isInitializing = ref(true);

// Watch for filter changes and automatically refetch topics data
watch(
    [page, pageSize, topicFilter, selectedTopicsDateFilter],
    async () => {
        if (destination.value && !isInitializing.value) {
            await fetchTopicsData();
        }
    },
    { deep: true }
);

// Watch for timeseries filter changes and automatically refetch timeseries data
watch(
    [selectedTimeseriesDateFilter, selectedTimeseriesTimeUnit],
    async () => {
        if (destination.value && !isInitializing.value) {
            await fetchTimeseriesData();
        }
    },
    { deep: true }
);

async function fetchTopicsData() {
    try {
        topicsLoading.value = true;
        removeWebSocketListeners();

        const isDefaultDateFilter =
            selectedTopicsDateFilter.value.key === FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME || !selectedTopicsDateFilter.value?.key;

        const dateFilterToUse = isDefaultDateFilter
            ? {}
            : {
                  start: selectedTopicsDateFilter.value?.value?.start,
                  end: selectedTopicsDateFilter.value?.value?.end,
                  key: selectedTopicsDateFilter.value?.key
              };

        const filterParams = topicFilter.value
            ? {
                  ...topicFilter.value.filterOption,
                  filterType: topicFilter.value.filter.name
              }
            : {};

        const params = {
            topic_list_from: page.value * pageSize.value,
            topic_list_size: pageSize.value,
            time_type: !isDefaultDateFilter ? 'timesummary' : undefined,
            ...dateFilterToUse,
            level: 'topic',
            ...filterParams
        };

        destinationLatestMetrics.value.topics = {};
        specificTopicMetricsToUse.value = await metrics.destinations.fetch(props.id, params);

        if (isDefaultDateFilter) {
            specificTopicMetricsToUse.value = null;
        }

        addWebSocketListeners();
    } finally {
        topicsLoading.value = false;
    }
}

async function fetchTimeseriesData() {
    try {
        timeseriesLoading.value = true;

        const isDefaultFilter =
            selectedTimeseriesDateFilter.value?.key === FILTER_DEFAULTS.DATE_FILTERS.LAST_24H || !selectedTimeseriesDateFilter.value?.key;

        const params = isDefaultFilter
            ? { timeUnit: selectedTimeseriesTimeUnit.value }
            : {
                  start: selectedTimeseriesDateFilter.value?.value?.start,
                  end: selectedTimeseriesDateFilter.value?.value?.end,
                  key: selectedTimeseriesDateFilter.value?.key,
                  timeUnit: selectedTimeseriesTimeUnit.value
              };

        const timeseriesData = await metrics.destinations.fetchTimeseriesData(props.id, params);
        plotSpecificMetrics.value = isDefaultFilter ? null : timeseriesData;
    } finally {
        timeseriesLoading.value = false;
    }
}

const chartOptions = computed(() => {
    const titleText = getTimeRangeLabel(selectedTimeseriesDateFilter.value?.key) || TIME_RANGE_LABELS.LAST_24H;

    const metricKeys = _.cloneDeep(selectedTimeseriesMetrics.value)?.map((x) => x.key) || [];

    const dataForEachSelectedMetric = metricKeys.map((metricKey) => {
        const metricData =
            _.isNil(plotSpecificMetrics.value) || _.isEmpty(plotSpecificMetrics.value)
                ? destinationTimeseriesConnectorMetrics.value[metricKey]
                : plotSpecificMetrics.value?.connector[metricKey];

        return {
            metricKey,
            metricData,
            metricInfo: getMetricFormattingInfo(metricKey, connectorInfo.value?.metrics)
        };
    });

    const seriesAndYAxes = dataForEachSelectedMetric.map((metric, index) => {
        const yAxis = {
            title: {
                text: metric.metricInfo?.name
            },
            labels: {
                formatter: function () {
                    return formatter(this.value, { unit: metric.metricInfo?.unit }, false).display;
                }
            },
            opposite: index % 2 !== 0
        };

        const series = {
            name: metric.metricInfo?.name,
            data: metric.metricData?.map((dataItem) => {
                const localTime = moment.utc(dataItem.timestamp).tz(moment.tz.guess()).toDate();
                return {
                    x: localTime,
                    y: dataItem.value
                };
            }),
            type: 'line',
            yAxis: index,
            custom: {
                unit: metric.metricInfo?.unit
            },
            dataLabels: {
                enabled: true,
                formatter: function () {
                    return formatter(this.y, { unit: metric.metricInfo?.unit }, false).display;
                }
            }
        };

        return { series, yAxis };
    });

    // Split the series and yAxis configurations
    const series = seriesAndYAxes.map((sy) => sy.series);
    const yAxis = seriesAndYAxes.map((sy) => sy.yAxis);

    const options = {
        time: {
            timezone: moment.tz.guess()
        },
        chart: {
            zoomType: 'xy'
        },
        title: {
            text: titleText
        },
        subtitle: {
            text:
                selectedTimeseriesTimeUnit.value === 'hour'
                    ? 'Hourly Data'
                    : selectedTimeseriesTimeUnit.value === 'minute'
                      ? 'Minute Data'
                      : selectedTimeseriesTimeUnit.value === 'month'
                        ? 'Monthly Data'
                        : 'Daily Data'
        },
        caption: {
            text: `Timezone: ${moment.tz.guess()}`
        },
        xAxis: {
            type: 'datetime',
            labels: {
                formatter: formatChartXAxisLabels
            }
        },
        yAxis: yAxis,
        series: series,
        tooltip: {
            shared: true,
            crosshairs: true,
            formatter: function () {
                const localTime = moment(new Date(this.x)).tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');

                let tooltip = `${localTime}<br>`;
                this.points.forEach((point) => {
                    tooltip += `<b>${point.series.name}:</b> ${
                        formatter(point.y, { unit: point.series.userOptions.custom?.unit }, false).display
                    }<br>`;
                });
                return tooltip;
            }
        }
    };

    return options;
});

const getFormattedValueForLatest = (metricKey, level = 'inline', children = null, dashOnRule = null, otherMetricSource = null) => {
    const isChildren = children || _.isNumber(children);

    if (!destination.value) return { display: '-' };

    const metricSource = otherMetricSource || destinationLatestMetrics.value;

    const metricValue = isChildren ? metricSource[level]?.[children]?.[metricKey] : metricSource[level]?.[metricKey];

    if (!connectorInfo.value) return { display: '-' };

    const metricFormattingInfo = getMetricFormattingInfo(metricKey, connectorInfo.value?.metrics);

    const formatterParams = [metricValue, metricFormattingInfo];

    if (dashOnRule || dashOnRule === false) {
        formatterParams.push(dashOnRule);
    }

    return {
        ...formatter(...formatterParams),
        metricDisplayName: metricFormattingInfo?.name
    };
};

async function loadDestinationUponAction() {
    tabLoading.value = true;
    metricsLoading.value = true;
    try {
        await Promise.all([
            destinationsStore.fetchConnectors(),
            metrics.destinations.fetch(props.id, {
                topic_list_from: page.value * pageSize.value,
                topic_list_size: pageSize.value,
                time_type: 'latest'
            })
        ]);

        const destinationVal = await destinationsStore.getById(props.id, null, true);

        destination.value = JSON.parse(JSON.stringify(destinationVal ?? {}));
        defaultDestination.value = _.cloneDeep(destination.value);
    } finally {
        tabLoading.value = false;
        metricsLoading.value = false;
    }
}

// async function onPause(id) {
//     try {
//         actionLoading.value = true;
//         await destinationsStore.pause(id);
//         await loadDestinationUponAction();
//     } finally {
//         actionLoading.value = false;
//     }
// }

async function onStop(id) {
    try {
        actionLoading.value = true;
        await destinationsStore.stop(id);
        await loadDestinationUponAction();
    } finally {
        actionLoading.value = false;
    }
}

async function onResume(id) {
    try {
        actionLoading.value = true;
        await destinationsStore.resume(id);
        await loadDestinationUponAction();
    } finally {
        actionLoading.value = false;
    }
}

async function onRestart(id) {
    try {
        actionLoading.value = true;
        await destinationsStore.restart(id);
        await loadDestinationUponAction();
    } finally {
        actionLoading.value = false;
    }
}

async function onReset(destination) {
    const message = `Are you sure you want to reset '<strong>${destination.name}</strong>'?<br><br>` +
        `<ul>` +
        `<li>The connector will stop and restart from the beginning of the input topics</li>` +
        `<li>1 week worth of data or more might be replayed</li>` +
        `<li>Default kafka retention in Streamkap is 7 days</li>` +
        `<li>Consumer lag alerts might be raised until the data backlog is replayed</li>` +
        `<li><strong>This action cannot be undone</strong></li>` +
        `</ul>`;

    try {
        await useConfirmBeforeAction('reset', async () => {
            actionLoading.value = true;
            await destinationsStore.reset(destination.id);
            await loadDestinationUponAction();
            actionLoading.value = false;
        }, {
            message,
            expectedInput: 'reset'
        });
    } catch (err) {
        console.log('Reset cancelled or failed:', err);
        actionLoading.value = false;
    }
}

function onClone(destination) {
    router.push({
        name: 'new-destination',
        params: { connector: destination.connector },
        query: { cloneConfig: JSON.stringify(destination.config) }
    });
}

async function onDelete(entity, name, id, index) {
    tabLoading.value = true;
    hidePromptTemporary.value = true;

    try {
        await useDeleteConfirmation({
            entity,
            name,
            title: `Delete ${entity}`,
            message: `Are you sure you want to delete the ${entity} <strong>"${name}"</strong>? This action cannot be undone and you will be redirected to the connectors page.`,
            onConfirm: async () => {
                await destinationsStore.delete(id);
                destinationsStore.fetch();
                router.push({ name: 'destinations-list' });
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    } finally {
        tabLoading.value = false;
        hidePromptTemporary.value = false;
    }
}

const stats = computed(() => {
    const latencyMetric = getFormattedValueForLatest('latency', 'connector');
    const eventsSentMetric = getFormattedValueForLatest('sinkRecordSendTotal', 'connector');
    const lagMetric = getFormattedValueForLatest('recordsLag', 'connector');

    return [
        {
            id: 0,
            name: 'Status',
            stat: destinationStatus.value,
            icon: CheckCircle,
            infoIcon: destinationStatus.value === 'Broken',
            errors: destinationErrors.value,
            colorClass: getStatusColor(destinationStatus.value, false, true)
        },
        {
            id: 1,
            name: latencyMetric.metricDisplayName,
            stat: latencyMetric.display,
            icon: Clock,
            colorClass: 'bg-chart-3' // Orange for Latency
        },
        {
            id: 2,
            name: lagMetric.metricDisplayName,
            stat: lagMetric.display,
            icon: Clock,
            colorClass: 'bg-chart-3' // Orange for Latency
        },
        {
            id: 3,
            name: eventsSentMetric.metricDisplayName,
            stat: eventsSentMetric.display,
            icon: ChevronsRight,
            colorClass: 'bg-chart-1' // Green for Events Received
        }
    ];
});

const destinationStatus = computed(() => {
    return (
        destinationLatestMetrics?.value?.inline?.connector_status ??
        destination?.value?.connector_status ??
        (destination?.value?.connector === 'kafkadirect' ? 'Active' : 'Unknown')
    );
});

let tabs = ref([
    { name: 'Status', href: '#' },
    { name: 'Auth', href: '#' },
    { name: 'Settings', href: '#' },
    {
        name: 'Logs',
        href: `/logs?destination_id=${props.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        newTabUrl: true
    }
]);

const setCurrentTab = (str) => {
    if (str === 'Logs') {
        return;
    }

    currentTab.value = str;
};

const destinationErrors = computed(() => {
    if (destinationStatus.value === 'Broken' && destination.value?.task_statuses) {
        return [...new Set(Object.values(destination.value?.task_statuses).map((task) => task.trace))];
    }

    return [];
});

const isDefaultTopicDateFilter = computed(() => {
    return selectedTopicsDateFilter.value.key === 'allTime' || !selectedTopicsDateFilter.value || !selectedTopicsDateFilter.value?.key;
});

// async function discardDestination() {
//     try {
//         tabLoading.value = true;

//         const destinationVal = await destinationsStore.getById(props.id);
//         destination.value = JSON.parse(JSON.stringify(destinationVal ?? {}));
//     } finally {
//         tabLoading.value = false;
//     }
// }

function onDiscard() {
    destination.value = _.cloneDeep(defaultDestination.value);
    canSave.value = false;
}

async function saveDestination() {
    tabLoading.value = true;
    formError.value = '';
    try {
        // Inject default config values for unchanged properties
        if (destinationConfig.value) {
            destination.value.config = destination.value.config || {};
            destinationConfig.value.forEach((control) => {
                const name = control.name;
                const defaultVal = control.value?.default;
                if (defaultVal !== undefined && destination.value.config[name] === undefined) {
                    destination.value.config[name] = defaultVal;
                }
            });
        }
        await destinationsStore.edit(destination.value, destination.value.id);
        await loadDestinationUponAction();
        canSave.value = false;

        toast.success('Destination successfully saved.');
    } catch (err) {
        console.error('Error in saveDestination:', err);
        formError.value = err;
        tabLoading.value = false;
    } finally {
        tabLoading.value = false;
    }
}

async function onTopicsPageChange(newPage) {
    page.value = newPage;
}

function onTopicsPerPageChange(newSize) {
    pageSize.value = newSize;
    page.value = 0; // Reset to first page when changing page size
}

async function handleTopicsDateFilterInput(dateFilter) {
    selectedTopicsDateFilter.value = dateFilter;
}

const scrollToCurrentPosition = async () => {
    const container = document.querySelector('.top-page-anchor');
    const scrollValue = _.cloneDeep(container?.scrollTop);

    await nextTick(() => {
        const latestContainer = document.querySelector('.top-page-anchor');
        const latestScrollValue = _.cloneDeep(latestContainer?.scrollTop);

        if (latestScrollValue !== scrollValue) {
            setTimeout(() => (container.scrollTop = scrollValue), 1); // Reset to captured position'
        }
    });
};
const downloadViews = async () => {
    viewsDownloading.value = true;
    try {
        let textTopicsSampleScript = await destinationsStore.getTopicSampleScripts(props.id);

        if (isJSON(textTopicsSampleScript) && typeof textTopicsSampleScript === 'object') {
            textTopicsSampleScript = JSON.stringify(textTopicsSampleScript, null, 2);
        }

        const blob = new Blob([textTopicsSampleScript], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stk_dest_${props.id?.trim()}sample_views.sql`;
        a.click();
        window.URL.revokeObjectURL(url);
    } catch (err) {
        // Error handled silently
    } finally {
        viewsDownloading.value = false;
    }
};

async function refreshTimeseries() {
    await fetchTimeseriesData();
    await scrollToCurrentPosition();
}

async function handleTimeseriesDateFilterInput(dateFilter, specificTimeUnit) {
    selectedTimeseriesDateFilter.value = dateFilter;
    selectedTimeseriesTimeUnit.value = specificTimeUnit;
    await scrollToCurrentPosition();
}

async function onTimeseriesTimeRangeChange(timeRangeValue) {
    const dateFilter = createDateFilterFromTimeRange(timeRangeValue);
    await handleTimeseriesDateFilterInput(dateFilter, dateFilter.timeUnit);
}

async function onTopicsTimeRangeChange(timeRangeValue) {
    const dateFilter = {
        type: 'custom',
        key: typeof timeRangeValue === 'string' ? timeRangeValue : `last${timeRangeValue}s`,
        value:
            typeof timeRangeValue === 'string'
                ? timeRangeValue
                : {
                      start: moment().utc().subtract(timeRangeValue, 'seconds').format('YYYY-MM-DDTHH:mm:ss.SSS'),
                      end: moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSS')
                  }
    };
    await handleTopicsDateFilterInput(dateFilter);
}

async function onCopyId(id) {
    try {
        await navigator.clipboard.writeText(id);
        toast.success('ID copied to clipboard');
    } catch (error) {
        console.error('Failed to copy ID to clipboard:', error);
    }
}
</script>

<template>
    <div>
        <SQLViewsModal
            v-if="openViewsModal"
            :loading="viewsLoading"
            :scripts="selectedTopicsScript"
            :is-modal-open="openViewsModal"
            @update:is-open="setViewsModelOpenStatus($event)" />

        <div v-if="destination">
            <PageBreadcrumb>
                <template #actions>
                    <ActionMenu
                        variant="menu"
                        menu-trigger-size="md"
                        :show-clone="userHasAccess([$Permissions.WRITE_DESTINATIONS], authStore.user)"
                        :show-delete="userHasAccess([$Permissions.DELETE_DESTINATIONS], authStore.user)"
                        :show-stop="
                            destination?.connector !== 'kafkadirect' &&
                            userHasAccess([$Permissions.TRIGGER_ACTION_DESTINATIONS], authStore.user) &&
                            destinationStatus !== 'Paused' &&
                            destinationStatus !== 'Stopped'
                        "
                        :show-resume="
                            destination?.connector !== 'kafkadirect' &&
                            userHasAccess([$Permissions.TRIGGER_ACTION_DESTINATIONS], authStore.user) &&
                            destinationStatus !== 'Active' &&
                            destinationStatus !== 'Broken'
                        "
                        :show-restart="
                            userHasAccess([$Permissions.TRIGGER_ACTION_DESTINATIONS], authStore.user) &&
                            destinationStatus !== 'Active' &&
                            destinationStatus !== 'Paused'
                        "
                        :show-copy-id="true"
                        :loading="actionLoading || tabLoading"
                        @clone="onClone(destination)"
                        @stop="onStop(destination.id)"
                        @delete="onDelete('destination', destination.name, destination.id)"
                        @resume="onResume(destination.id)"
                        @restart="onRestart(destination.id)"
                        @copyId="onCopyId(destination.id)" />
                </template>
            </PageBreadcrumb>
        </div>
        <div>
            <AppLoader :listen="['destinations.fetch']" :force-loading="generalLoading" default-height="60vh" class="min-h-60">
                <div v-if="destination" class="w-full top-page-anchor">
                    <Tabs :tabs="tabs" :initial-active-tab="currentTab" :loading="tabLoading" @tab-change="setCurrentTab">
                        <template #Status>
                            <div class="mx-auto w-full mt-5">
                                <div class="mb-5">
                                    <div class="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-4">
                                        <StatCard
                                            v-for="item in stats"
                                            :key="item.id"
                                            :icon-bg-class="item.colorClass"
                                            :info-icon="item.infoIcon"
                                            :errors="item.errors"
                                            :icon="item.icon"
                                            :loading="metricsLoading || configurationLoading"
                                            :title="item.name"
                                            :value="item.stat" />
                                    </div>
                                </div>

                                <Card class="bg-sidebar-primary-foreground">
                                    <CardHeader>
                                        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                            <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                                                <TimeRangeFilter
                                                    label="Time Range"
                                                    :model-value="convertFilterKeyToTimeRangeValue(selectedTimeseriesDateFilter?.key || 'last24h')"
                                                    :disabled="timeseriesLoading || metricsLoading"
                                                    placeholder="Select time range"
                                                    :include-live-option="false"
                                                    :include-all-time-option="false"
                                                    :include-date-options="true"
                                                    :include-short-ranges="false"
                                                    :hide-options="[
                                                        'today',
                                                        'yesterday',
                                                        FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME,
                                                        'lastWeek',
                                                        'lastMonth',
                                                        'thisWeek',
                                                        'thisMonth'
                                                    ]"
                                                    @update:model-value="onTimeseriesTimeRangeChange" />
                                                <SelectDropdown
                                                    label="Time Grain"
                                                    :options="timeUnitOptions"
                                                    :model-value="selectedTimeseriesTimeUnit"
                                                    :disabled="timeseriesLoading"
                                                    @update:model-value="
                                                        (value) => handleTimeseriesDateFilterInput(selectedTimeseriesDateFilter, value)
                                                    " />
                                            </div>
                                            <div class="flex flex-col space-y-1 min-w-0 sm:max-w-xs">
                                                <div class="text-xs text-muted-foreground">Metrics</div>
                                                <MultiSelect
                                                    v-model="selectedMetricKeys"
                                                    :options="listOfTimeseriesMetricOptions"
                                                    :placeholder="
                                                        timeseriesLoading
                                                            ? 'Loading...'
                                                            : listOfTimeseriesMetricOptions?.length
                                                              ? 'Select metrics'
                                                              : 'No metrics available'
                                                    "
                                                    :disabled="timeseriesLoading || !listOfTimeseriesMetricOptions?.length"
                                                    :option-label="'name'"
                                                    :track-by="'key'"
                                                    :show-tags="false"
                                                    :enable-refresh="true"
                                                    @refresh="refreshTimeseries()" />
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent class="pt-0">
                                        <HighChart ref="chart" :chart-options="chartOptions" :loading="timeseriesLoading || metricsLoading" />
                                    </CardContent>
                                </Card>

                                <div class="mt-5">
                                    <div>
                                        <div class="w-full flex justify-between items-center mb-3">
                                            <div class="flex items-center gap-3">
                                                <h4 class="font-medium">Topics</h4>
                                            </div>
                                            <div class="flex items-center gap-3">
                                                <TimeRangeFilter
                                                    v-model="selectedTopicsTimeRange"
                                                    :loading="topicsLoading"
                                                    placeholder="Select time range"
                                                    :include-live-option="false"
                                                    :include-all-time-option="true"
                                                    :include-date-options="true"
                                                    :include-short-ranges="false"
                                                    :hide-options="['today', 'yesterday']"
                                                    @update:model-value="onTopicsTimeRangeChange" />
                                                <ItemsPerPageSelect
                                                    v-model="pageSize"
                                                    :loading="topicsLoading"
                                                    include-small-sizes
                                                    @update:model-value="onTopicsPerPageChange" />
                                            </div>
                                        </div>

                                        <ScrollArea
                                            class="w-full border shadow-md rounded-md"
                                            :class="
                                                ((metricsLoading || topicsLoading) && pageSize <= 10) ||
                                                (!metricsLoading && !topicsLoading && (topics(specificTopicMetricsToUse)?.length || 0) <= 10)
                                                    ? 'h-auto'
                                                    : 'h-50-dynamic'
                                            ">
                                            <Table>
                                                <TableHeader class="sticky top-0 bg-background z-10">
                                                    <TableRow class="bg-muted/50">
                                                        <TableHead class="first:rounded-tl-md">Topic</TableHead>
                                                        <TableHead v-for="metric in availableMetrics?.topic" :key="metric.attribute">
                                                            {{ metric.display.name }}
                                                        </TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    <!-- Skeleton rows when loading -->
                                                    <template v-if="metricsLoading || topicsLoading">
                                                        <TableRow v-for="n in pageSize || 5" :key="`skeleton-${n}`">
                                                            <TableCell>
                                                                <SkeletonLoader preset="table-name" :loading="true" />
                                                            </TableCell>
                                                            <TableCell
                                                                v-for="metric in availableMetrics?.topic || [
                                                                    { attribute: 'metric1' },
                                                                    { attribute: 'metric2' },
                                                                    { attribute: 'metric3' }
                                                                ]"
                                                                :key="metric.attribute">
                                                                <SkeletonLoader preset="table-metric" :loading="true" />
                                                            </TableCell>
                                                        </TableRow>
                                                    </template>
                                                    <!-- Actual data rows -->
                                                    <template v-else>
                                                        <TableRow v-for="topic in topics(specificTopicMetricsToUse)" :key="topic.id">
                                                            <TableCell>
                                                                <span>{{ topic.name }}</span>
                                                            </TableCell>
                                                            <TableCell v-for="metric in availableMetrics?.topic" :key="metric.attribute">
                                                                <span>
                                                                    {{
                                                                        getFormattedValueForLatest(
                                                                            metric.attribute,
                                                                            'topics',
                                                                            topic.id,
                                                                            null,
                                                                            specificTopicMetricsToUse
                                                                        ).display
                                                                    }}
                                                                </span>
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableEmpty
                                                            v-if="!metricsLoading && !topicsLoading && !topics(specificTopicMetricsToUse)?.length"
                                                            :colspan="(availableMetrics?.topic?.length || 0) + 1">
                                                            <TableEmptyState />
                                                        </TableEmpty>
                                                    </template>
                                                </TableBody>
                                            </Table>
                                            <ScrollBar orientation="horizontal" />
                                            <ScrollBar orientation="vertical" />
                                        </ScrollArea>

                                        <TablePagination
                                            class="mb-5"
                                            :current-page="page"
                                            :page-size="pageSize"
                                            :current-page-items-count="topics(specificTopicMetricsToUse)?.length"
                                            :total-items="destination.topics?.length ?? 0"
                                            :loading="metricsLoading"
                                            :message="{
                                                show: !metricsLoading,
                                                showCustomMessage: false,
                                                firstPart: 'Showing topics',
                                                middlePart: 'to',
                                                lastPart: `of ${destination.topics?.length ?? 0}`
                                            }"
                                            item-type="topics"
                                            @page-change="onTopicsPageChange" />
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template #Auth>
                            <div class="mt-5 w-full">
                                <FormSkeleton v-if="configurationLoading || !destinationConfig" :field-count="3" />
                                <template v-else>
                                    <ConnectorTab
                                        v-if="destination.config && destinationConfig"
                                        :key="destination.config"
                                        :connector="destination"
                                        :default-connector="defaultDestination"
                                        :config="destinationConfig"
                                        :justify-content="'start'"
                                        :tab="'auth'"
                                        :entity-type="'destination'"
                                        :edit-mode="true"
                                        @changed="(status) => (canSave = status)" />
                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'edit-destination'" />
                                </template>
                            </div>
                        </template>
                        <template #Settings>
                            <div class="mt-5 w-full">
                                <FormSkeleton v-if="configurationLoading || !destinationConfig" :field-count="6" />
                                <template v-else>
                                    <ConnectorTab
                                        v-if="destination.config && destinationConfig"
                                        :key="destination.config"
                                        :connector="destination"
                                        :default-connector="defaultDestination"
                                        :config="destinationConfig"
                                        :justify-content="'start'"
                                        :tab="'settings'"
                                        :entity-type="'destination'"
                                        :edit-mode="true"
                                        @changed="(status) => (canSave = status)" />
                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'edit-destination'" />
                                </template>
                            </div>
                        </template>
                        <template #smt>
                            <div class="mt-5 w-full">
                                <FormSkeleton v-if="configurationLoading || !destinationConfig" :field-count="4" />
                                <template v-else>
                                    <ConnectorTab
                                        v-if="destination.config && destinationConfig"
                                        :key="destination.config"
                                        :connector="destination"
                                        :default-connector="defaultDestination"
                                        :config="destinationConfig"
                                        :justify-content="'start'"
                                        :tab="'smt'"
                                        :entity-type="'destination'"
                                        :edit-mode="true"
                                        @changed="(status) => (canSave = status)" />
                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'edit-destination'" />
                                </template>
                            </div>
                        </template>
                    </Tabs>
                </div>
            </AppLoader>
        </div>

        <!-- Footer will teleport to App.vue footer slot -->
        <div v-if="destination" v-can-access.permissions="[$Permissions.WRITE_DESTINATIONS]">
            <AppFooter>
                <template #right>
                    <ActionMenu
                        variant="row"
                        :show-save="true"
                        :disable-save="!canSave || tabLoading || !destination.name"
                        :loading="actionLoading || tabLoading"
                        @save="saveDestination"
                        @discard="onDiscard" />
                </template>
            </AppFooter>
        </div>
    </div>
</template>
